/**
 * Stage B — Per-patient enrollment in an installed trial.
 *
 * Idempotent: keyed on UNIQUE (patient_id, trial_site_id) on
 * patient_trial_site_assignments. Safe to re-run.
 *
 * Pipeline:
 *   1. UPSERT patient_trial_site_assignments
 *        (patient_id = profiles.user_id, profile_id = profiles.id)
 *   2. Resolve the trial's protocol_id and expand patient_visits rows
 *      from study_visits using a baseline date (default = today).
 *   3. Optional: invoke `randomise-subject` edge fn to assign an arm.
 *   4. Write a 21 CFR Part 11 audit row.
 *
 * Skips ecrf/ecoa assignment per-patient — Stage A already provisioned
 * the visit→form bundles for the site, so forms surface automatically
 * once a patient_visit exists for that visit_code.
 */

import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

type SupabaseLike = typeof supabase;


export interface EnrollPatientInput {
  /** profiles.id of the patient (NOT auth user id) */
  patientProfileId: string;
  trialSiteId: string;
  /** cro_trials.id — also referenced from trial_protocols.trial_id */
  trialId: string;
  croTrialId?: string;
  /** ISO date string for Day 0 of the visit schedule. Defaults to today. */
  baselineDate?: string;
  workflowStage?: 'screening' | 'enrolled' | 'randomized';
  randomise?: boolean;
}

export interface EnrollPatientResult {
  assignmentId: string;
  patientVisitsCreated: number;
  randomized: boolean;
  arm: string | null;
}

type ProtocolVisit = {
  visit_code: string | null;
  visit_name: string | null;
  visit_type: string | null;
  day_number: number | null;
  display_order: number | null;
};

function addDaysISO(baseISO: string, days: number): string {
  const d = new Date(baseISO + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function numberOrNull(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) return Number(value);
  return null;
}

function normalizeVisitType(value: unknown, index: number): 'screening' | 'baseline' | 'week_4' | 'week_8' | 'month_6' | 'end_of_study' | 'unscheduled' | 'adverse_event' {
  const raw = String(value || '').toLowerCase();
  if (raw.includes('screen')) return 'screening';
  if (raw.includes('base') || raw.includes('random')) return 'baseline';
  if (raw.includes('week 4') || raw.includes('week_4') || raw.includes('day 28')) return 'week_4';
  if (raw.includes('week 8') || raw.includes('week_8') || raw.includes('day 56')) return 'week_8';
  if (raw.includes('month 6') || raw.includes('month_6')) return 'month_6';
  if (raw.includes('adverse')) return 'adverse_event';
  if (raw.includes('unscheduled')) return 'unscheduled';
  if (raw.includes('end') || raw.includes('eos') || raw.includes('early termination')) return 'end_of_study';
  return index === 0 ? 'screening' : 'unscheduled';
}

function normalizeProtocolJsonVisits(raw: unknown): ProtocolVisit[] {
  const arr = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? (raw as { visits?: unknown[] }).visits : null);
  if (!Array.isArray(arr)) return [];
  return arr.map((item, index) => {
    const v = item && typeof item === 'object' ? item as Record<string, unknown> : {};
    const visitName = String(v.visit_name ?? v.name ?? v.label ?? v.visit ?? v.visit_code ?? v.code ?? `Visit ${index + 1}`).trim();
    const visitCodeRaw = v.visit_code ?? v.code ?? null;
    const visitCode = visitCodeRaw == null || !String(visitCodeRaw).trim() ? `V${index + 1}` : String(visitCodeRaw).trim();
    return {
      visit_code: visitCode,
      visit_name: visitName,
      visit_type: String(v.visit_type ?? v.type ?? ''),
      day_number: numberOrNull(v.day_number ?? v.day ?? v.study_day) ?? index * 30,
      display_order: index,
    };
  });
}

async function insertPatientVisits(client: SupabaseLike, input: EnrollPatientInput, baseline: string, visits: ProtocolVisit[], registryTrialId: string | null): Promise<{ created: number; available: number }> {
  if (visits.length === 0) return { created: 0, available: 0 };
  const { data: existingPv } = await client
    .from('patient_visits')
    .select('visit_number')
    .eq('patient_id', input.patientProfileId)
    .eq('trial_site_id', input.trialSiteId);
  const have = new Set((existingPv ?? []).map((r) => r.visit_number));

  const rows = visits
    .map((v, idx) => ({
      patient_id: input.patientProfileId,
      trial_site_id: input.trialSiteId,
      ...(registryTrialId ? { trial_id: registryTrialId } : {}),
      visit_type: normalizeVisitType(v.visit_type || v.visit_name || v.visit_code, idx),
      visit_number: v.display_order ?? idx,
      scheduled_date: addDaysISO(baseline, v.day_number ?? idx * 30),
      notes: v.visit_name || v.visit_code || `Visit ${idx + 1}`,
    }))
    .filter((r) => !have.has(r.visit_number));

  if (rows.length === 0) return { created: 0, available: visits.length };
  const { error } = await client.from('patient_visits').insert(rows as any);
  if (error) throw error;
  return { created: rows.length, available: visits.length };
}


async function sha256Hex(s: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function enrollPatientInTrial(client: SupabaseLike, input: EnrollPatientInput): Promise<EnrollPatientResult> {
  const baseline = input.baselineDate ?? new Date().toISOString().slice(0, 10);
  const stage = input.workflowStage ?? 'screening';

  // 1. Resolve auth user_id for the patient (required by FK)
  const { data: prof, error: profErr } = await client
    .from('profiles')
    .select('id, user_id')
    .eq('id', input.patientProfileId)
    .single();
  if (profErr) throw profErr;
  if (!prof?.user_id) throw new Error('Patient profile is missing a user_id');

  // 2. UPSERT assignment row (idempotent via unique(patient_id, trial_site_id))
  const { data: existing } = await client
    .from('patient_trial_site_assignments')
    .select('id')
    .eq('patient_id', prof.user_id)
    .eq('trial_site_id', input.trialSiteId)
    .maybeSingle();

  let assignmentId = existing?.id ?? '';
  if (assignmentId) {
    const { error } = await client
      .from('patient_trial_site_assignments')
      .update({
        workflow_stage: stage,
        cro_trial_id: input.croTrialId ?? input.trialId,
        is_active_enrollment: true,
        assignment_status: 'active',
      })
      .eq('id', assignmentId);
    if (error) throw error;
  } else {
    const { data: ins, error } = await client
      .from('patient_trial_site_assignments')
      .insert({
        patient_id: prof.user_id,
        profile_id: prof.id,
        trial_site_id: input.trialSiteId,
        cro_trial_id: input.croTrialId ?? input.trialId,
        workflow_stage: stage,
        assignment_status: 'active',
        assignment_source: 'install_orchestrator',
        is_active_enrollment: true,
        assigned_at: new Date().toISOString(),
      })
      .select('id')
      .single();
    if (error) throw error;
    assignmentId = ins.id;
  }

  // 3. Expand patient_visits from site-scoped study_visits first.
  let patientVisitsCreated = 0;
  let protocolVisitsAvailable = 0;
  const { data: croTrial } = await client
    .from('cro_trials')
    .select('id, trial_id, nct_id, protocol_version')
    .eq('id', input.trialId)
    .maybeSingle();

  // Registry trial used by monitor access; only set when the trial row is confirmed.
  const registryCandidate = (croTrial as any)?.trial_id ?? input.trialId;
  const { data: registryTrial } = await client
    .from('trials')
    .select('id')
    .eq('id', registryCandidate)
    .maybeSingle();
  const registryTrialId = (registryTrial as any)?.id ?? null;

  let trialProtocol: any = null;
  const { data: protocolForCroTrial } = await client
    .from('trial_protocols')
    .select('id, visit_schedule, eligibility_requirements')
    .eq('trial_id', input.trialId)
    .maybeSingle();
  trialProtocol = protocolForCroTrial ?? null;

  if (!trialProtocol && (croTrial as any)?.trial_id) {
    const { data: protocolForPublicTrial } = await client
      .from('trial_protocols')
      .select('id, visit_schedule, eligibility_requirements')
      .eq('trial_id', (croTrial as any).trial_id)
      .maybeSingle();
    trialProtocol = protocolForPublicTrial ?? null;
  }

  const candidateProtocolNumbers = new Set(
    [
      (trialProtocol?.eligibility_requirements as any)?.protocol_number,
      (croTrial as any)?.nct_id,
      (croTrial as any)?.protocol_version,
    ]
      .map((v) => String(v || '').trim().toLowerCase())
      .filter(Boolean),
  );

  const { data: studyProtocols } = await client
    .from('study_protocols')
    .select('id, protocol_number, created_at')
    .eq('trial_site_id', input.trialSiteId)
    .order('created_at', { ascending: false } as any);

  const studyProtocol =
    (studyProtocols ?? []).find((p: any) => candidateProtocolNumbers.has(String(p.protocol_number || '').trim().toLowerCase()))
    ?? ((studyProtocols ?? []).length === 1 ? (studyProtocols as any)?.[0] : null);

  if (studyProtocol?.id) {
    const { data: visits } = await client
      .from('study_visits')
      .select('visit_code, visit_name, visit_type, day_number, display_order')
      .eq('protocol_id', studyProtocol.id)
      .order('display_order', { ascending: true } as any);

    if (visits && visits.length > 0) {
      const inserted = await insertPatientVisits(client, input, baseline, visits as any, registryTrialId);
      patientVisitsCreated = inserted.created;
      protocolVisitsAvailable = inserted.available;
    }
  }

  if (protocolVisitsAvailable === 0) {
    const jsonVisits = normalizeProtocolJsonVisits(trialProtocol?.visit_schedule);
    if (jsonVisits.length > 0) {
      const inserted = await insertPatientVisits(client, input, baseline, jsonVisits, registryTrialId);
      patientVisitsCreated = inserted.created;
      protocolVisitsAvailable = inserted.available;
    }
  }

  if (protocolVisitsAvailable === 0) {
    throw new Error('No protocol visit schedule is available for this trial/site. Run AI extraction or install the trial before enrolling/randomizing patients.');
  }

  // 4. Optional randomisation
  let randomized = false;
  let arm: string | null = null;
  if (input.randomise) {
    try {
      const { data, error } = await (client as any).functions.invoke('randomise-subject', {
        body: {
          trial_id: input.trialId,
          trial_site_id: input.trialSiteId,
          patient_id: input.patientProfileId,
        },
      });
      if (!error && data) {
        const d = data as any;
        randomized = true;
        arm = d?.arm ?? d?.allocation?.arm ?? null;
      }
    } catch {
      // Randomisation is advisory at enrollment time
    }
  }

  // 5. Audit
  try {
    const { data: { user } } = await (client as any).auth.getUser();
    const payload = { input, result: { assignmentId, patientVisitsCreated, randomized, arm }, ts: new Date().toISOString() };
    await (client as any).from('compliance_audit_trail').insert({
      action: 'patient_enroll_in_trial',
      entity_type: 'patient_trial_site_assignments',
      entity_id: assignmentId,
      user_id: user?.id ?? null,
      changes: payload,
      hash_after: await sha256Hex(JSON.stringify(payload)),
      metadata: { trial_id: input.trialId, trial_site_id: input.trialSiteId },
    });
  } catch {
    // Audit best-effort
  }

  return { assignmentId, patientVisitsCreated, randomized, arm };
}

export function useEnrollPatientInTrial() {
  return useMutation({
    mutationFn: (input: EnrollPatientInput) => enrollPatientInTrial(supabase, input),
  });
}

